/**
 * Gaming Report Model
 */

define(function(require, exports, module) {
    'use strict';

    var $ = require('j');
    var utils = require('utils');
    var _ = require('underscore');
    var csv = require('util/csv.util');
    var i18n = require('i18n');
    var BaseModel = require('common/base.model');
    var server = require('server');
    var user = require('user');

    module.exports = BaseModel.extend({

        defaults: {
            year: (new Date()).getUTCFullYear()
        },

        getStatistics: function(year) {

            var state = $.Deferred();

            if (year) {
                this.set('year', year);
            }

            server.send({
                ID: 32029,
                description: 'Get Player Yearly Statistics',
                responses: [32030, 32031],
                year: this.get('year')
            }).done(function(data) {
                state.resolve(this.parseData(data));
            }.bind(this))
                .fail(state.reject);

            return state;
        },

        parseData: function(data) {
            const { date, formatCurrency } = window.Playtech.API.formatter;
            var currentDate = this.get('year') === this.getCurrentDate().getUTCFullYear() ? this.getCurrentDate() : new Date(this.get('year'), 11, 31),
                userDetails = user.get('userDetails'),
                customerName = userDetails.title ? userDetails.title + '.' : '',
                startPeriod = date(new Date(currentDate.getUTCFullYear(), 0, 1), '%d-%m-%Y'),
                endPeriod = date(currentDate, '%d-%m-%Y');

            if (userDetails.fullFirstname) {
                customerName += ' ' + userDetails.fullFirstname + ' ' + userDetails.fullFirstSurname;
                if (userDetails.fullSecondSurname) {
                    customerName += ' ' + userDetails.fullSecondSurname;
                }
            } else {
                customerName += ' ' + userDetails.firstName + ' ' + userDetails.lastName;
            }

            var result = {
                years: this.getAvailableYears(),
                data: {
                    period: {
                        label: i18n.t('playtech.mobile.system.mobile-cashier.gamingReport.period.label'),
                        value: i18n.t('playtech.mobile.system.mobile-cashier.gamingReport.period.value', startPeriod, endPeriod)
                    },
                    customer: {
                        label: i18n.t('playtech.mobile.system.mobile-cashier.gamingReport.customer'),
                        value: customerName
                    },
                    username: {
                        label: i18n.t('playtech.mobile.system.mobile-cashier.gamingReport.username'),
                        value: user.get('userName')
                    },
                    startBalance: {
                        label: i18n.t('playtech.mobile.system.mobile-cashier.gamingReport.startBalance', startPeriod),
                        value: formatCurrency(data.data.initialBalance, {currencyCode: data.data.currencyCode})
                    },
                    currentBalance: {
                        label: i18n.t('playtech.mobile.system.mobile-cashier.gamingReport.currentBalance', endPeriod),
                        value: formatCurrency(data.data.finalBalance, {currencyCode: data.data.currencyCode})
                    },
                    totalDeposits: {
                        label: i18n.t('playtech.mobile.system.mobile-cashier.gamingReport.totalDeposits'),
                        value: formatCurrency(data.data.totalDeposits, {currencyCode: data.data.currencyCode})
                    },
                    totalWithdrawals: {
                        label: i18n.t('playtech.mobile.system.mobile-cashier.gamingReport.totalWithdrawals'),
                        value: formatCurrency(data.data.totalWithdrawals, {currencyCode: data.data.currencyCode})
                    },
                    totalBonusesRedeemed: {
                        label: i18n.t('playtech.mobile.system.mobile-cashier.gamingReport.totalBonusesRedeemed'),
                        value: formatCurrency(data.data.totalBonusesRedeemed, {currencyCode: data.data.currencyCode})
                    }
                }
            };

            _.each(data.data.totalBets, function(bet) {
                result.data['bet' + bet.product.toUpperCase()] = {
                    label: i18n.t('playtech.mobile.system.mobile-cashier.gamingReport.bet.' + bet.product),
                    value: formatCurrency(bet.amount, {currencyCode: data.data.currencyCode})
                };
            });

            _.each(data.data.totalWins, function(win) {
                result.data['win' + win.product.toUpperCase()] = {
                    label: i18n.t('playtech.mobile.system.mobile-cashier.gamingReport.win.' + win.product),
                    value: formatCurrency(win.amount, {currencyCode: data.data.currencyCode})
                };
            });

            result.exportFileName = 'gaming_report_' + startPeriod + '_' + endPeriod + '.csv';
            result.exportFile = this.generateCSV(result.data);

            return result;
        },

        getEmptyData: function() {
            var currentDate = this.get('year') === this.getCurrentDate().getUTCFullYear() ? this.getCurrentDate() : new Date(this.get('year'), 11, 31),
                startPeriod = window.Playtech.API.formatter.date(new Date(currentDate.getUTCFullYear(), 0, 1), '%d-%m-%Y'),
                endPeriod = window.Playtech.API.formatter.date(currentDate, '%d-%m-%Y');

            return {
                years: this.getAvailableYears(),
                data: null,
                exportFileName: 'gaming_report_' + startPeriod + '_' + endPeriod,
                exportFile: this.generateCSV({})
            };
        },

        getAvailableYears: function() {
            var signUpYear = parseInt(window.Playtech.API.formatter.date(user.get('userDetails').signupDate, '%Y')),
                currentYear = this.getCurrentDate().getUTCFullYear(),
                years = [];

            for (var i = signUpYear; i <= currentYear; i++) {
                years.push(i);
            }

            return years;
        },

        getCurrentDate: function() {
            return new Date();
        },

        getSpanishUserIdentityData: function() {
            var userDetails = user.get('userDetails');

            return ['personalId', 'passportId', 'idCardNumber', 'driversLicenceNumber'].reduce(function(data, field) {
                if (userDetails[field] && !data.type) {
                    data.type = utils.capitalize(field); // capitalize to match translation key
                    data.value = userDetails[field];
                }

                return data;
            }, {
                type: null,
                value: null,
                firstName: userDetails.firstName,
                lastName: userDetails.lastName
            });
        },

        generateCSV: function(data) {
            var csvData = [],
                labelItem = i18n.t('playtech.mobile.system.mobile-cashier.gamingReport.item'),
                amountLabel = i18n.t('playtech.mobile.system.mobile-cashier.gamingReport.amount');

            _.each(data, function(item) {
                var obj = {};
                obj[labelItem] = item.label;
                obj[amountLabel] = item.value && item.value.replace(',', '.') || '';
                csvData.push(obj);
            });

            return csv.jsonToCsv(csvData);
        }
    });

});
